import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to make Slack and Twilio talk to each other",
  "description": "",
  "published": "2016-10-18T00:00:00.000Z",
  "redirect_from": ["/blog/make-slack-twilio-talk/swizec/7128"],
  "categories": "Technical",
  "hero": "./img/lFiUoxb.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Why? Because it’s cool.`}</p>
    <p>{`And in some cases, you can make a bot that answers the door. Like I `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-22-a-door-answering-slackbot/swizec/7038"
      }}>{`started here`}</a>{` and `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/livecoding-23-slackbots-oauth/swizec/7065"
      }}>{`continued here`}</a>{`. This isn’t a series. Don’t worry :)`}</p>
    <p>{`You should start by setting up a `}<a parentName="p" {...{
        "href": "https://www.twilio.com/"
      }}>{`Twilio account`}</a>{` and a `}<a parentName="p" {...{
        "href": "https://api.slack.com/slack-apps"
      }}>{`Slack app`}</a>{`. We’re going to build a node.js server running `}<a parentName="p" {...{
        "href": "http://expressjs.com/"
      }}>{`Express`}</a>{` that:`}</p>
    <ol>
      <li parentName="ol">{`Takes a phone call`}</li>
      <li parentName="ol">{`Records what the caller says`}</li>
      <li parentName="ol">{`Puts the caller on hold`}</li>
      <li parentName="ol">{`Posts a message with buttons on Slack`}</li>
      <li parentName="ol">{`Tells caller which button was pressed`}</li>
      <li parentName="ol">{`Hangs up`}</li>
    </ol>
    <p>{`Let’s assume you’ve already set up an Express project. It’s easy to do from scratch, and if you already have an existing one that you’re adding Slack and Twilio to, then you don’t need this step.`}</p>
    <h2 {...{
      "id": "slack-oauth"
    }}>{`Slack OAuth`}</h2>
    <p>{`The first thing we have to take care of is Slack’s OAuth dance. We’re going to use the `}<inlineCode parentName="p">{`grant-express`}</inlineCode>{` plugin to add OAuth to our server, then do the dance to get a Slack token.`}</p>
    <p>{`Run:`}</p>
    <p>{`$ npm install --save grant-express express-session`}</p>
    <p>{`This installs an OAuth plugin and a session middleware for Express. The first is for OAuth dancing, and the second is for storing information between API calls temporarily.`}</p>
    <p>{`To enable the dance, add this to your `}<inlineCode parentName="p">{`app.js`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// /app.js
let session = require("express-session"),
  Grant = require("grant-express");
let grant = new Grant({
  server: {
    protocol: "https",
    host: "swizec.ngrok.io", // use your domain name
    callback: "/callback",
    transport: "session",
    state: true,
  },
  slack: {
    key: YOUR_SLACK_KEY,
    secret: YOUR_SLACK_SECRET,
    scope: [
      "chat:write:bot",
      "chat:write:user",
      "channels:read",
      "commands",
      "incoming-webhook",
    ],
    callback: "/handle_slack_callback",
  },
});
app.use(session({ secret: "grant", resave: false, saveUninitialized: true }));
app.use(grant);
`}</code></pre>
    <p><inlineCode parentName="p">{`grant-express`}</inlineCode>{` comes half configured for Slack out of the box. You just have to tell it which scopes you’re going to use, what your access keys and secrets are (you find them in your config on Slack), and your server’s domain. For local development, I like to use `}<a parentName="p" {...{
        "href": "https://ngrok.com/"
      }}>{`ngrok`}</a>{`, hence `}<inlineCode parentName="p">{`swizec.ngrok.io`}</inlineCode>{`.`}</p>
    <p>{`Once you have that configured, you tell Express to use `}<inlineCode parentName="p">{`grant`}</inlineCode>{` and `}<inlineCode parentName="p">{`session`}</inlineCode>{` with two `}<inlineCode parentName="p">{`app.use`}</inlineCode>{` calls.`}</p>
    <p>{`To complete the dance, you need a `}<inlineCode parentName="p">{`/handle_slack_callback`}</inlineCode>{` route, which goes in your `}<inlineCode parentName="p">{`routes/index.js`}</inlineCode>{` file, if you’re as careless as I am. In a bigger app, you’d want to be more organized.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// /routes/index.js
router.get("/handle_slack_callback", (req, res) => {
  console.log(req.session.grant.response);

  // this is a good opportunity to save the token in a DB of some sort
  res.end(JSON.stringify(req.session.grant.response, null, 2));
});
`}</code></pre>
    <p>{`Now you can go to `}<inlineCode parentName="p">{`https://swizec.ngrok.io/connect/slack`}</inlineCode>{` and do the dance.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2016/10/oauth-dance.gif",
        "alt": "oauth-dance"
      }}></img></p>
    <p>{`Save that `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. It lets you post to Slack as an app.`}</p>
    <p>{`Now that you’ve seen mine, you can make a thing that keeps posting to my test Slack channel. That would be mean of you but also kinda cool in that `}<em parentName="p">{`“Whoa, the internet is humans!”`}</em>{` kind of way.`}</p>
    <h2 {...{
      "id": "twilio-→-slack-→-twilio"
    }}>{`Twilio → Slack → Twilio`}</h2>
    <p>{`Now comes the tricky part: making Slack and Twilio talk to each other. Here’s a diagram that explains the flow of events.`}</p>
    <p>{`The key trick to reading this diagrams: the vertical line is time. Starts on top, flows down.`}</p>
    <p><img parentName="p" {...{
        "src": "/42c4349b611a739a8317f17ae74b7587/lE932wgif08159c.gif",
        "alt": null
      }}></img></p>
    <p>{`Step-by-step in code, that gif looks like this:`}</p>
    <h3 {...{
      "id": "phone-call-from-twilio"
    }}>{`Phone call from Twilio`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// routes/index.js
router.post("/call", function (req, res, next) {
  const caller = req.body.Caller;
  const callSid = req.body.CallSid;
  const twilio = require("twilio");
  let twiml = new twilio.TwimlResponse();
  twiml.say("Hello! State your name, then press any key.", { voice: "alice" });
  twiml.record({
    action: \`/call/recording/\${callSid}\`,
    maxLength: 60,
  });
  res.type("text/xml");
  res.send(twiml.toString());
});
`}</code></pre>
    <p>{`TwiML is Twilio’s XML format that you can use to tell their servers what to do with a phone call. In this case we’re saying a greeting and asking Twilio to record what a person says.`}</p>
    <h3 {...{
      "id": "get-recording--send-slack"
    }}>{`Get recording + send Slack`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// routes/index.js
router.post("/call/recording/:callSid", (req, res, next) => {
  const callSid = req.params.callSid;
  const twiml = new twilio.TwimlResponse();
  const recordingUrl = req.body.RecordingUrl;

  const twilio = require("twilio"),
    WebClient = require("@slack/client").WebClient;
  const webSlack = new WebClient(TOKEN_FROM_BEFORE);
  // Sets up Slack message
  let data = {
    attachments: [
      {
        fallback: "Somebody is at the door",
        title: "Somebody is at the door",
        title_link: recordingUrl,
        text: "Click link to hear the recording",
        callback_id: \`door_open:\${callSid}\`,
        actions: [
          {
            name: "open_door",
            text: "Let them in",
            type: "button",
            value: "open_door",
          },
          {
            name: "deny_access",
            text: "No.",
            type: "button",
            value: "deny_access",
          },
        ],
      },
    ],
  };
  /* Post on Slack, pause Twilio phone call */
  webSlack.chat.postMessage("#bot-testing", "", data, () => {
    twiml.say("Thank you. Please hold.", { voice: "alice" });
    twiml.pause({ length: 240 });
    twiml.say("Sorry, nobody pressed the button. Try calling a real human.", {
      voice: "alice",
    });
    res.type("text/xml");
    res.send(twiml.toString());
  });
});
`}</code></pre>
    <p>{`Most of this code is setting up the Slack message. We send it to Slack and in the callback, when we know it’s sent, we use TwiML to ask Twilio to pause the call for 240 seconds.`}</p>
    <p>{`If the time is up, we say something. In most cases, the pause will be interrupted by a Slack callback.`}</p>
    <p>{`The message we sent looks like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "703px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/242e2/lFiUoxb.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "15.695067264573993%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'63\\'%20viewBox=\\'0%200%20400%2063\\'%20preserveAspectRatio=\\'none\\'/%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/ca0a1/lFiUoxb.webp 223w", "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/75680/lFiUoxb.webp 445w", "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/b1708/lFiUoxb.webp 703w"],
              "sizes": "(max-width: 703px) 100vw, 703px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/e92b6/lFiUoxb.png 223w", "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/e66bf/lFiUoxb.png 445w", "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/242e2/lFiUoxb.png 703w"],
              "sizes": "(max-width: 703px) 100vw, 703px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/5b7c37d3c2b88d8fe2466fcefb5cce9e/242e2/lFiUoxb.png",
              "alt": "lFiUoxb",
              "title": "lFiUoxb",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <h3 {...{
      "id": "slack-button-pressed"
    }}>{`Slack button pressed`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// router/index.js
router.post("/slack/response", (req, res, next) => {
  const payload = JSON.parse(req.body.payload);
  const callSid = payload.callback_id.split(":")[1];
  const action = payload.actions[0];
  const twilio = require("twilio");
  const client = twilio(settings.twilio.accountSid, settings.twilio.authToken); // you get these in your Twilio dashboard
  let continueAt = "",
    lettingIn = false;
  if (action.value === "open_door") {
    continueAt = "call/open_the_door";
    lettingIn = true;
  } else {
    continueAt = "call/dont_open_door";
    lettingIn = false;
  }
  client.calls(callSid).update(
    {
      url: \`https://swizec.ngrok.io/\${continueAt}\`,
      method: "POST",
    },
    (err, call) => {
      res.send({
        text: lettingIn ? "Letting them in" : "Telling them to go away",
      });
    }
  );
});
`}</code></pre>
    <p>{`We find out which button was pressed in `}<inlineCode parentName="p">{`payload.actions`}</inlineCode>{`. They’re the same strings as we set up earlier – `}<inlineCode parentName="p">{`open_door`}</inlineCode>{` is a `}<inlineCode parentName="p">{`Yes`}</inlineCode>{`, anything else is a `}<inlineCode parentName="p">{`No`}</inlineCode>{`.`}</p>
    <p><inlineCode parentName="p">{`callSid`}</inlineCode>{` tells us which phone call we’re responding to. That’s why we put it in `}<inlineCode parentName="p">{`callback_id`}</inlineCode>{` earlier :)`}</p>
    <p>{`With `}<inlineCode parentName="p">{`client.calls(callSid)`}</inlineCode>{`, we ask Twilio to let us continue the paused phone call. For some reason, we can’t directly tell the API what to say, but instead we have to set up more routes in our server.`}</p>
    <p>{`In the callback, when we know Twilio knows what we want, we send a response to Slack, which updates the message that a user clicked on.`}</p>
    <h3 {...{
      "id": "finish-the-call"
    }}>{`Finish the call`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// routes/index.js
const twilio = require("twilio");
router.post("/call/open_the_door", (req, res, next) => {
  const twiml = new twilio.TwimlResponse();
  twiml.say("Greetings! Come on up.", { voice: "alice" });
  twiml.play({ digits: 9 });
  res.type("text/xml");
  res.send(twiml.toString());
});
router.post("/call/dont_open_door", (req, res, next) => {
  const twiml = new twilio.TwimlResponse();
  twiml.say("Sorry, nobody pressed the button. Try calling a real human.", {
    voice: "alice",
  });
  res.type("text/xml");
  res.send(twiml.toString());
});
`}</code></pre>
    <p>{`You can guess what this does, can’t you? If the `}<inlineCode parentName="p">{`Yes`}</inlineCode>{` button was pressed, we tell the caller to come in and send a dial tone. If the `}<inlineCode parentName="p">{`No`}</inlineCode>{` button was pressed, we ask the caller to try contacting a human.`}</p>
    <p>{`In theory, that dial tone asks the building buzzer to open the door. But I haven’t tried this in the wild yet. ?`}</p>
    <p>{`Happy slacktwillying!`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      